import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  deleteVoiceScan,
  getVoiceScanDetails,
  getVoiceScanImage,
  getVoiceScanReports,
  initiateVoiceScanSession,
  uploadVoiceRecording,
} from './api';
import {
  VoiceReportDetail,
  VoiceScanReportResponse,
  VoiceUploadPayload,
} from './type';

export const useInfiniteGetVoiceReports = (
  profileId: string,
  initialPageParam: number
) => {
  return useInfiniteQuery({
    queryKey: ['inifinite-voice-reports', profileId],
    queryFn: async ({ pageParam }) => {
      const response = await getVoiceScanReports({
        page_number: pageParam,
        page_size: 10,
      });
      let parsedResponse: VoiceScanReportResponse = {
        reading_data: [],
        count: 0,
      };
      try {
        const parsed = JSON.parse(response);
        parsedResponse = parsed?.count
          ? parsed
          : { reading_data: [], count: 0 };
      } catch (error) {
        console.log('Failed to parse voice scan response', error);
      }

      return parsedResponse;
    },
    initialPageParam,
    getNextPageParam: (lastPage, allPages) => {
      const allData = allPages.flatMap((page) => page?.reading_data);
      if (allData.length >= lastPage?.count) return undefined;

      return allPages.length + 1;
    },
    select: ({ pages }) => pages.flatMap((page) => page.reading_data),
    gcTime: 0,
    staleTime: 0,
  });
};

interface GetSymptomReportDetail extends UseQueryOptions {
  sessionId: string;
}
export const useGetVoiceReportDetail = (
  props: Omit<GetSymptomReportDetail, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: ['voice-scan-detail', props.sessionId],
    queryFn: () => getVoiceScanDetails(props.sessionId),
    enabled: !!props.sessionId,
    staleTime: Infinity,
    gcTime: Infinity,
    ...props,
  }) as UseQueryResult<VoiceReportDetail>;
};

type DeleteVoiceReportArgs = Omit<
  UseMutationOptions<
    { success: boolean },
    Error,
    { session_id: string[] },
    unknown
  >,
  'mutationFn'
>;
export const useDeleteVoiceReport = (props: DeleteVoiceReportArgs) => {
  return useMutation({
    mutationKey: ['delete-voice-scan'],
    mutationFn: deleteVoiceScan,
    ...props,
  });
};

export const useGetVoiceScanImage = () => {
  return useQuery({
    queryKey: ['voice-scan-image'],
    queryFn: getVoiceScanImage,
  });
};

type IntiateVoiceScanArgs = Omit<
  UseMutationOptions<
    { session_id: string },
    Error,
    { imageId: string },
    unknown
  >,
  'mutationFn'
>;
export const useInitiateVoiceScan = (props: IntiateVoiceScanArgs) => {
  return useMutation({
    mutationKey: ['initiate-voice-scan'],
    mutationFn: initiateVoiceScanSession,
    ...props,
  });
};

type UploadVoiceRecordingArgs = Omit<
  UseMutationOptions<unknown, Error, VoiceUploadPayload, unknown>,
  'mutationFn'
>;
export const useUploadVoiceRecording = (props: UploadVoiceRecordingArgs) => {
  return useMutation({
    mutationKey: ['upload-voice-recording'],
    mutationFn: uploadVoiceRecording,
    ...props,
  });
};
